import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { detachedDeepFreeze } from './freeze';
import { serializeDiagnosticContextToMarkdown } from './markdown';
import { safelyObserveDiagnostics } from './safe-observation';
import { sanitizeDiagnosticContext, sanitizeDiagnosticContribution } from './sanitize';
import {
  DIAGNOSTIC_LIMITS,
  DIAGNOSTIC_SCHEMA_VERSION,
  DiagnosticContextContribution,
  DiagnosticContextV1,
  DiagnosticContributorGetter,
  DiagnosticError,
  DiagnosticLauncherPosition,
  DiagnosticMoneyValue,
  DiagnosticPricingContext,
  DiagnosticPricingDiscrepancy,
  DiagnosticPricingInput,
} from './types';

const CONTRIBUTOR_ORDER = [
  'capture',
  'variants',
  'user',
  'store',
  'cart',
  'cart-surface',
  'cart-display',
  'pdp-one-menu',
  'pdp-legacy',
] as const;

type PricingAttemptId = number | null;

export interface DiagnosticPricingRecorder {
  beginAttempt: (
    sourceApi: DiagnosticPricingContext['sourceApi'],
    input?: DiagnosticPricingInput
  ) => PricingAttemptId;
  recordRequest: (attemptId: PricingAttemptId, input?: DiagnosticPricingInput) => void;
  recordMutationResult: (
    attemptId: PricingAttemptId,
    result: { rbiOrderId?: string; pricingStatus?: string; error?: DiagnosticError }
  ) => void;
  recordPollResult: (
    attemptId: PricingAttemptId,
    result: {
      phase: 'polling' | 'succeeded' | 'failed';
      rbiOrderId?: string;
      pricingStatus?: string;
      orderStatus?: string;
      deliveryStatus?: string;
      returnedTotal?: DiagnosticMoneyValue;
      error?: DiagnosticError;
    }
  ) => void;
  recordFailure: (attemptId: PricingAttemptId, error?: DiagnosticError) => void;
  recordDiscrepancy: (discrepancy: DiagnosticPricingDiscrepancy) => void;
  clear: () => void;
}

interface HostRegistration {
  id: string;
  isModal: boolean;
  sequence: number;
}

interface DiagnosticContextValue {
  enabled: boolean;
  isOpen: boolean;
  snapshot: DiagnosticContextV1 | null;
  markdown: string | null;
  launcherPosition: DiagnosticLauncherPosition | null;
  activeHostId: string | null;
  registerContributor: (id: string, getter: DiagnosticContributorGetter) => () => void;
  open: () => void;
  refresh: () => void;
  close: () => void;
  setLauncherPosition: (position: DiagnosticLauncherPosition | null) => void;
  updatePrincipal: (principalKey: string) => void;
  registerHost: (id: string, isModal: boolean) => () => void;
  pricing: DiagnosticPricingRecorder;
}

interface DiagnosticActivityContextValue {
  enabled: boolean;
  registerContributor: DiagnosticContextValue['registerContributor'];
  updatePrincipal: DiagnosticContextValue['updatePrincipal'];
  pricing: DiagnosticPricingRecorder;
}

const noop = () => undefined;
const noopUnregister = () => noop;
const EMPTY_PRICING_RECORDER: DiagnosticPricingRecorder = {
  beginAttempt: () => null,
  recordRequest: noop,
  recordMutationResult: noop,
  recordPollResult: noop,
  recordFailure: noop,
  recordDiscrepancy: noop,
  clear: noop,
};

const DiagnosticContext = createContext<DiagnosticContextValue>({
  enabled: false,
  isOpen: false,
  snapshot: null,
  markdown: null,
  launcherPosition: null,
  activeHostId: null,
  registerContributor: noopUnregister,
  open: noop,
  refresh: noop,
  close: noop,
  setLauncherPosition: noop,
  updatePrincipal: noop,
  registerHost: noopUnregister,
  pricing: EMPTY_PRICING_RECORDER,
});

const DiagnosticActivityContext = createContext<DiagnosticActivityContextValue>({
  enabled: false,
  registerContributor: noopUnregister,
  updatePrincipal: noop,
  pricing: EMPTY_PRICING_RECORDER,
});

const mergeContribution = (
  target: DiagnosticContextContribution,
  addition: DiagnosticContextContribution
): DiagnosticContextContribution =>
  ({
    ...target,
    ...addition,
    capture:
      target.capture || addition.capture
        ? { ...(target.capture ?? { capturedAt: new Date().toISOString() }), ...addition.capture }
        : undefined,
    user: target.user || addition.user ? { ...target.user, ...addition.user } : undefined,
    store: target.store || addition.store ? { ...target.store, ...addition.store } : undefined,
    pdp: target.pdp || addition.pdp ? { ...target.pdp, ...addition.pdp } : undefined,
    cart: target.cart || addition.cart ? { ...target.cart, ...addition.cart } : undefined,
    pricing:
      target.pricing || addition.pricing ? { ...target.pricing, ...addition.pricing } : undefined,
  } as DiagnosticContextContribution);

const sortContributors = (
  [a]: [string, DiagnosticContributorGetter],
  [b]: [string, DiagnosticContributorGetter]
) => {
  const getOrder = (id: string) => {
    const matchingPrefix = [...CONTRIBUTOR_ORDER]
      .filter(prefix => id.startsWith(prefix))
      .sort((left, right) => right.length - left.length)[0];
    return matchingPrefix ? CONTRIBUTOR_ORDER.indexOf(matchingPrefix) : CONTRIBUTOR_ORDER.length;
  };
  const aIndex = getOrder(a);
  const bIndex = getOrder(b);
  return aIndex - bIndex || a.localeCompare(b);
};

type ActiveDiagnosticSurface = 'pdp' | 'cart' | null;

const getActiveDiagnosticSurface = (
  entries: Array<[string, DiagnosticContributorGetter]>
): ActiveDiagnosticSurface => {
  if (entries.some(([id]) => id.startsWith('pdp-'))) {
    return 'pdp';
  }
  return entries.some(([id]) => id.startsWith('cart-surface') || id.startsWith('cart-display'))
    ? 'cart'
    : null;
};

const shouldCollectContributor = (id: string, activeSurface: ActiveDiagnosticSurface): boolean =>
  !id.startsWith('cart-') || activeSurface === 'cart';

export const DiagnosticContextProvider = ({ children }: PropsWithChildren) => {
  const enabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS));
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const contributorsRef = useRef(new Map<string, DiagnosticContributorGetter[]>());
  const pricingRef = useRef<DiagnosticPricingContext | null>(null);
  const pricingDiscrepancyRef = useRef<DiagnosticPricingDiscrepancy | null>(null);
  const attemptSequenceRef = useRef(0);
  const principalRef = useRef<string | undefined>(undefined);
  const hostSequenceRef = useRef(0);
  const [hosts, setHosts] = useState<HostRegistration[]>([]);
  const [snapshot, setSnapshot] = useState<DiagnosticContextV1 | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [launcherPosition, setLauncherPositionState] = useState<DiagnosticLauncherPosition | null>(
    null
  );

  const isOpen = Boolean(snapshot && markdown);

  const discardDisplayedSnapshot = useCallback(() => {
    setSnapshot(null);
    setMarkdown(null);
  }, []);

  const clearAll = useCallback(() => {
    discardDisplayedSnapshot();
    pricingRef.current = null;
    pricingDiscrepancyRef.current = null;
    setLauncherPositionState(null);
  }, [discardDisplayedSnapshot]);

  useEffect(() => {
    clearAll();
  }, [clearAll, enabled]);

  const registerContributor = useCallback((id: string, getter: DiagnosticContributorGetter) => {
    const owners = contributorsRef.current.get(id) ?? [];
    contributorsRef.current.set(id, [...owners, getter]);

    return () => {
      const currentOwners = contributorsRef.current.get(id);
      const ownerIndex = currentOwners?.indexOf(getter) ?? -1;
      if (!currentOwners || ownerIndex < 0) {
        return;
      }
      const remainingOwners = [
        ...currentOwners.slice(0, ownerIndex),
        ...currentOwners.slice(ownerIndex + 1),
      ];
      if (remainingOwners.length) {
        contributorsRef.current.set(id, remainingOwners);
      } else {
        contributorsRef.current.delete(id);
      }
    };
  }, []);

  const collect = useCallback(() => {
    if (!enabledRef.current) {
      return;
    }
    let contribution: DiagnosticContextContribution = {};
    const warnings = Array.from(contributorsRef.current.entries())
      .filter(([, owners]) => owners.length > 1)
      .slice(0, DIAGNOSTIC_LIMITS.warnings)
      .map(([id]) => `Contributor ${id.slice(0, DIAGNOSTIC_LIMITS.label)} has duplicate owners.`);
    const entries = Array.from(contributorsRef.current.entries())
      .map(([id, owners]): [string, DiagnosticContributorGetter] => [id, owners[0]])
      .sort(sortContributors);
    const activeSurface = getActiveDiagnosticSurface(entries);
    for (const [id, getter] of entries) {
      if (!shouldCollectContributor(id, activeSurface)) {
        continue;
      }
      try {
        const current = getter();
        if (current) {
          contribution = mergeContribution(contribution, sanitizeDiagnosticContribution(current));
        }
      } catch {
        if (warnings.length < DIAGNOSTIC_LIMITS.warnings) {
          warnings.push(`Contributor ${id.slice(0, DIAGNOSTIC_LIMITS.label)} was unavailable.`);
        }
      }
    }
    const sanitized = sanitizeDiagnosticContext({
      schemaVersion: DIAGNOSTIC_SCHEMA_VERSION,
      ...contribution,
      ...(pricingRef.current ? { pricing: pricingRef.current } : {}),
      ...(pricingDiscrepancyRef.current
        ? { pricingDiscrepancy: pricingDiscrepancyRef.current }
        : {}),
      ...(warnings.length ? { warnings } : {}),
    });
    const frozen = detachedDeepFreeze(sanitized) as DiagnosticContextV1;
    setSnapshot(frozen);
    setMarkdown(serializeDiagnosticContextToMarkdown(frozen));
  }, []);

  const open = useCallback(() => {
    if (!snapshot && enabledRef.current) {
      safelyObserveDiagnostics(collect);
    }
  }, [collect, snapshot]);

  const refresh = useCallback(() => {
    if (enabledRef.current) {
      safelyObserveDiagnostics(collect);
    }
  }, [collect]);

  const updatePrincipal = useCallback(
    (principalKey: string) => {
      if (principalRef.current === undefined) {
        principalRef.current = principalKey;
        return;
      }
      if (principalRef.current !== principalKey) {
        principalRef.current = principalKey;
        discardDisplayedSnapshot();
        pricingRef.current = null;
        pricingDiscrepancyRef.current = null;
      }
    },
    [discardDisplayedSnapshot]
  );

  const setLauncherPosition = useCallback((position: DiagnosticLauncherPosition | null) => {
    if (enabledRef.current) {
      setLauncherPositionState(position);
    }
  }, []);

  const beginAttempt: DiagnosticPricingRecorder['beginAttempt'] = useCallback(
    (sourceApi, input) => {
      if (!enabledRef.current) {
        return null;
      }
      try {
        const logicalAttemptId = ++attemptSequenceRef.current;
        const now = new Date().toISOString();
        pricingDiscrepancyRef.current = null;
        pricingRef.current = sanitizeDiagnosticContext({
          schemaVersion: DIAGNOSTIC_SCHEMA_VERSION,
          pricing: {
            logicalAttemptId,
            sourceApi,
            attemptStartedAt: now,
            latestRequestAt: now,
            requestCount: 1,
            retryCount: 0,
            phase: 'requesting',
            input,
          },
        }).pricing!;
        return logicalAttemptId;
      } catch {
        pricingRef.current = null;
        return null;
      }
    },
    []
  );

  const updatePricing = useCallback(
    (attemptId: PricingAttemptId, update: Partial<DiagnosticPricingContext>) => {
      if (
        !enabledRef.current ||
        attemptId === null ||
        pricingRef.current?.logicalAttemptId !== attemptId
      ) {
        return;
      }
      try {
        pricingRef.current = sanitizeDiagnosticContext({
          schemaVersion: DIAGNOSTIC_SCHEMA_VERSION,
          pricing: { ...pricingRef.current, ...update },
        }).pricing!;
      } catch {
        // Diagnostic observation must never affect the pricing path it observes.
      }
    },
    []
  );

  const pricing = useMemo<DiagnosticPricingRecorder>(
    () => ({
      beginAttempt,
      recordRequest: (attemptId, input) => {
        const current = pricingRef.current;
        updatePricing(attemptId, {
          latestRequestAt: new Date().toISOString(),
          requestCount: (current?.requestCount ?? 0) + 1,
          retryCount: Math.max(0, current?.requestCount ?? 1),
          phase: 'requesting',
          input: { ...current?.input, ...input },
        });
      },
      recordMutationResult: (attemptId, result) =>
        updatePricing(attemptId, {
          rbiOrderId: result.rbiOrderId,
          pricingStatus: result.pricingStatus,
          phase: result.error ? 'failed' : result.rbiOrderId ? 'polling' : 'failed',
          error: result.error,
        }),
      recordPollResult: (attemptId, result) => updatePricing(attemptId, result),
      recordFailure: (attemptId, error) => updatePricing(attemptId, { phase: 'failed', error }),
      recordDiscrepancy: discrepancy => {
        if (!enabledRef.current) {
          return;
        }
        try {
          pricingDiscrepancyRef.current =
            sanitizeDiagnosticContext({
              schemaVersion: DIAGNOSTIC_SCHEMA_VERSION,
              pricingDiscrepancy: discrepancy,
            }).pricingDiscrepancy ?? null;
        } catch {
          // Diagnostic observation must never affect the mismatch or pricing path it observes.
        }
      },
      clear: () => {
        if (enabledRef.current) {
          pricingRef.current = null;
          pricingDiscrepancyRef.current = null;
        }
      },
    }),
    [beginAttempt, updatePricing]
  );

  const registerHost = useCallback((id: string, isModal: boolean) => {
    const host = { id, isModal, sequence: ++hostSequenceRef.current };
    setHosts(current => [...current.filter(item => item.id !== id), host]);
    return () => setHosts(current => current.filter(item => item.id !== id));
  }, []);

  const activeHostId = useMemo(() => {
    const modalHost = [...hosts]
      .filter(host => host.isModal)
      .sort((a, b) => b.sequence - a.sequence)[0];
    if (modalHost) {
      return modalHost.id;
    }
    return (
      [...hosts].filter(host => !host.isModal).sort((a, b) => b.sequence - a.sequence)[0]?.id ??
      null
    );
  }, [hosts]);

  const value = useMemo<DiagnosticContextValue>(
    () => ({
      enabled,
      isOpen,
      snapshot,
      markdown,
      launcherPosition,
      activeHostId,
      registerContributor,
      open,
      refresh,
      close: discardDisplayedSnapshot,
      setLauncherPosition,
      updatePrincipal,
      registerHost,
      pricing,
    }),
    [
      activeHostId,
      discardDisplayedSnapshot,
      enabled,
      isOpen,
      launcherPosition,
      markdown,
      open,
      pricing,
      refresh,
      registerContributor,
      registerHost,
      setLauncherPosition,
      snapshot,
      updatePrincipal,
    ]
  );

  const activityValue = useMemo<DiagnosticActivityContextValue>(
    () => ({ enabled, registerContributor, updatePrincipal, pricing }),
    [enabled, pricing, registerContributor, updatePrincipal]
  );

  return (
    <DiagnosticActivityContext.Provider value={activityValue}>
      <DiagnosticContext.Provider value={value}>{children}</DiagnosticContext.Provider>
    </DiagnosticActivityContext.Provider>
  );
};

export const useDiagnosticContext = () => useContext(DiagnosticContext);

const useDiagnosticActivity = () => useContext(DiagnosticActivityContext);

export const useDiagnosticToolsEnabled = () => useDiagnosticActivity().enabled;

export const useDiagnosticPrincipalUpdater = () => useDiagnosticActivity().updatePrincipal;

export const useDiagnosticPricingRecorder = () => useDiagnosticActivity().pricing;

export const useDiagnosticContributor = (id: string, getter: DiagnosticContributorGetter): void => {
  const { enabled, registerContributor } = useDiagnosticActivity();
  const getterRef = useRef(getter);
  getterRef.current = getter;
  useEffect(() => {
    if (!enabled) {
      return;
    }
    return registerContributor(id, () => getterRef.current());
  }, [enabled, id, registerContributor]);
};
